// src/theme/glassDialog.js
// The card-level frosted-glass treatment, in the two shapes it is consumed in:
// glassDialogSlotProps (Dialog Paper + backdrop) and glassCardSx (a bare sx
// fragment for plain Box/Paper surfaces). Both read theme.custom.wizard.

/**
 * Card-level frosted glass as an sx fragment — the surface treatment /tilaa's
 * wizard card defines. Spread it, then layer layout on top:
 *
 *   sx={(theme) => ({ ...glassCardSx(theme), p: 3 })}
 *
 * Both @supports branches are tested and BOTH properties set: Safari 16/17 ship
 * only -webkit-backdrop-filter, so guarding the unprefixed property alone drops
 * the blur there and silently falls the surface back to flat.
 *
 * Note the units: cardRadius is emitted as an explicit `${n}px` string. Passed
 * to sx as a bare number it would be multiplied by theme.shape.borderRadius (6).
 */
export function glassCardSx(theme) {
    const w = theme.custom?.wizard ?? {};
    return {
        background: w.cardBgFallback,
        border: w.cardBorder,
        borderRadius: w.cardRadius ? `${w.cardRadius}px` : undefined,
        boxShadow: w.cardShadow,
        "@supports (backdrop-filter: blur(20px)) or (-webkit-backdrop-filter: blur(20px))": {
            background: w.cardBg,
            backdropFilter: w.cardBlur,
            WebkitBackdropFilter: w.cardBlur,
        },
    };
}

export function glassDialogSlotProps(theme) {
    const w = theme.custom?.wizard ?? {};
    const easing = w.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    return {
        paper: {
            sx: {
                background: w.cardBg,
                backdropFilter: w.cardBlur,
                WebkitBackdropFilter: w.cardBlur,
                border: w.cardBorder,
                borderRadius: w.cardRadius ? `${w.cardRadius}px` : undefined,
                boxShadow: w.cardShadow,
                backgroundImage: "none",
                // Branded entrance: the glass panel rises as it fades into focus.
                // The Fade transition (tuned below) owns opacity; this keyframe
                // animates transform only, so the two compose without fighting —
                // and a rise (not a scale) avoids re-sampling the backdrop blur.
                // Runs on each open since MUI remounts the Paper. Reduced motion
                // drops the rise and leaves the crossfade as the fallback.
                animation: `bjDialogIn 280ms ${easing}`,
                "@keyframes bjDialogIn": {
                    from: { transform: "translateY(7px)" },
                    to: { transform: "translateY(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                "@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))": {
                    background: w.cardBgFallback,
                },
            },
        },
        backdrop: {
            sx: {
                backgroundColor: "rgba(0,0,0,0.55)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
            },
        },
        // Tune the built-in Fade to ease-out-quint, matched to the rise on enter
        // and faster on exit (exits should undercut entrances).
        transition: {
            timeout: { enter: 280, exit: 180 },
            easing: { enter: easing, exit: easing },
        },
    };
}
